import React, { Suspense, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';

// THE CLIPBOARD ON THE FRONT DESK (2026-11-01).
//
// Khari, at HQ: "there is nothing in the UI to tell me to click on Lyra to start
// a conversation. Please add a button on a standing clipboard on the desk."
//
// A clipboard on a little stand on the ledge, turned to the door: a sheet that
// says who can help, and a button that glows gently. Tapping any of it opens the
// same conversation tapping her does (FrontDesk onTap → her chat scene). Hand-
// lettered in marker, like the set list (VenueSetList) and the host signs — the
// World's own font, nothing fetched from elsewhere.
//
// Where it stands: rooms.js `frontDesk.clipboard` (room space at the room's fit
// × `k`, like the desk mark).
const FONT = `${import.meta.env.BASE_URL || '/'}fonts/PermanentMarker-Regular.ttf`;
const W = 0.15;          // ≈33 cm across (a person is ~0.75u): a sign as much as a clipboard
const H = 0.18;          // ≈40 cm tall
const LEAN = 0.38;       // leaning back on its stand, toward a standing eye line
const INK = '#16120e';
const AMBER = '#ffb25e';

export default function DeskClipboard({ spec, k = 1, name, onTap }) {
  const [hover, setHover] = useState(false);
  const button = useRef(null);
  useFrame(({ clock }) => {
    const m = button.current;
    if (!m) return;
    m.emissiveIntensity = hover ? 1.15 : 0.55 + 0.3 * Math.sin(clock.elapsedTime * 2.4);
  });
  if (!spec?.pos) return null;
  const pos = spec.pos.map((v) => v * k);
  const first = String(name || '').split(/\s+[-–—|]\s+/)[0].split(' ')[0] || 'us';
  const PW = W - 0.016; const PH = H - 0.034;   // the sheet

  const over = (e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; };
  const out = () => { setHover(false); document.body.style.cursor = ''; };
  const tap = (e) => { e.stopPropagation(); out(); onTap?.(); };

  return (
    <group position={pos} rotation={[0, spec.rotY || 0, 0]} onPointerOver={over} onPointerOut={out} onClick={tap}>
      {/* The stand: a black foot on the ledge, and a strut from its back up to
          the board's back (0.12 up, just behind the board at that height). */}
      <mesh position={[0, 0.003, -0.035]} castShadow>
        <boxGeometry args={[W * 0.72, 0.006, 0.13]} />
        <meshStandardMaterial color="#121212" roughness={0.45} metalness={0.3} />
      </mesh>
      <mesh position={[0, 0.06, -0.071]} rotation={[0.38, 0, 0]}>
        <boxGeometry args={[0.012, 0.129, 0.006]} />
        <meshStandardMaterial color="#121212" roughness={0.45} metalness={0.3} />
      </mesh>
      {/* The board, leaning back: its face tips up toward whoever stands at the desk. */}
      <group position={[0, 0.006, 0.008]} rotation={[-LEAN, 0, 0]}>
        <mesh position={[0, H / 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[W, H, 0.006]} />
          <meshStandardMaterial color="#7a5432" roughness={0.75} />
        </mesh>
        {/* The sheet. */}
        <mesh position={[0, H / 2 - 0.009, 0.0034]}>
          <planeGeometry args={[PW, PH]} />
          <meshStandardMaterial color="#f4efe4" roughness={0.95} emissive="#5c5648" emissiveIntensity={0.5} />
        </mesh>
        {/* The clip. */}
        <mesh position={[0, H - 0.008, 0.006]}>
          <boxGeometry args={[W * 0.42, 0.02, 0.008]} />
          <meshStandardMaterial color="#c9ccd1" roughness={0.25} metalness={0.9} />
        </mesh>
        {/* The button: lit, and brighter under the pointer. */}
        <mesh position={[0, H / 2 - 0.054, 0.0046]}>
          <planeGeometry args={[PW - 0.016, 0.036]} />
          <meshStandardMaterial ref={button} color={AMBER} emissive={AMBER} emissiveIntensity={0.6} roughness={0.5} />
        </mesh>
        <Suspense fallback={null}>
          <group position={[0, H / 2 - 0.009, 0.004]}>
            <Text font={FONT} fontSize={0.0125} color={INK} anchorX="center" anchorY="middle" position={[0, 0.05, 0.0005]} rotation={[0, 0, -0.02]} whiteSpace="nowrap">
              HAVE A QUESTION?
            </Text>
            <Text font={FONT} fontSize={0.0098} color="#4a4038" anchorX="center" anchorY="middle" position={[0, 0.017, 0.0005]} maxWidth={PW - 0.02} textAlign="center" lineHeight={1.15}>
              {`${first} at the desk can help`}
            </Text>
            <Text font={FONT} fontSize={0.0118} color="#1a0f00" anchorX="center" anchorY="middle" position={[0, -0.045, 0.0012]} whiteSpace="nowrap">
              {`TALK TO ${first.toUpperCase()}  ›`}
            </Text>
          </group>
        </Suspense>
      </group>
    </group>
  );
}
